/**
 * Export Lexical EditorState to Telegram-compatible HTML.
 *
 * The output uses only the HTML tags that Telegram's HTML parse mode
 * supports. No CSS, no classes (except `language-*` on code blocks),
 * no data attributes.
 */
import type { EditorState } from 'lexical';
import {
  walkTree,
  isInCodeBlock,
  type TextSegment,
  type ElementContext,
  type InlineFormat,
} from './tree-walker';

/**
 * HTML tag mapping for inline formats.
 * Order matters for nesting: outermost to innermost.
 */
const TAG_ORDER: InlineFormat[] = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'code',
];

const FORMAT_TAGS: Record<InlineFormat, [string, string]> = {
  bold: ['<b>', '</b>'],
  italic: ['<i>', '</i>'],
  underline: ['<u>', '</u>'],
  strikethrough: ['<s>', '</s>'],
  code: ['<code>', '</code>'],
};

/**
 * Escapes HTML entities in text.
 * Only three characters need escaping in Telegram HTML.
 */
function escapeHTML(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Gets opening and closing HTML tags for an element context.
 */
function getElementTags(
  context: ElementContext
): [string | null, string | null] {
  switch (context.type) {
    case 'spoiler':
      return ['<tg-spoiler>', '</tg-spoiler>'];
    case 'blockquote':
      return ['<blockquote>', '</blockquote>'];
    case 'expandable_blockquote':
      return ['<blockquote expandable>', '</blockquote>'];
    case 'link':
      if (context.userId !== undefined) {
        return [`<a href="tg://user?id=${String(context.userId)}">`, '</a>'];
      }
      return [`<a href="${escapeHTML(context.url || '')}">`, '</a>'];
    case 'custom_emoji':
      return [
        `<tg-emoji emoji-id="${escapeHTML(context.customEmojiId || '')}">`,
        '</tg-emoji>',
      ];
    case 'code_block':
      // Handled separately
      return [null, null];
    default:
      return [null, null];
  }
}

/**
 * Renders a segment with inline formatting tags.
 */
function renderSegmentWithFormats(
  text: string,
  formats: Set<InlineFormat>,
  isCodeBlock: boolean
): string {
  // Inside code blocks, no formatting tags are applied
  if (isCodeBlock) {
    return escapeHTML(text);
  }

  // Skip empty text
  if (text.length === 0) return '';

  // Build tag stack in order
  const openTags: string[] = [];
  const closeTags: string[] = [];

  for (const format of TAG_ORDER) {
    if (formats.has(format)) {
      const [open, close] = FORMAT_TAGS[format];
      openTags.push(open);
      closeTags.unshift(close); // Close in reverse order
    }
  }

  return openTags.join('') + escapeHTML(text) + closeTags.join('');
}

/**
 * Builds HTML from segments, handling element context transitions.
 */
function buildHTML(segments: TextSegment[]): string {
  const parts: string[] = [];
  let previousContext: ElementContext[] = [];
  let codeBlockLang: string | undefined;

  for (const segment of segments) {
    const currentContext = segment.elementContext;
    const segmentInCodeBlock = isInCodeBlock(segment);

    // Handle element context transitions

    // Close contexts that are no longer active (in reverse order)
    const closedContexts = previousContext.filter(
      (prev) => !currentContext.some((curr) => curr.key === prev.key)
    );

    for (let i = closedContexts.length - 1; i >= 0; i--) {
      const ctx = closedContexts[i];
      if (!ctx) continue;
      if (ctx.type === 'code_block') {
        // Close code block
        if (codeBlockLang) {
          parts.push('</code></pre>');
        } else {
          parts.push('</pre>');
        }
        codeBlockLang = undefined;
      } else {
        const [, closeTag] = getElementTags(ctx);
        if (closeTag) parts.push(closeTag);
      }
    }

    // Open new contexts
    const openedContexts = currentContext.filter(
      (curr) => !previousContext.some((prev) => prev.key === curr.key)
    );

    for (const ctx of openedContexts) {
      if (ctx.type === 'code_block') {
        // Open code block
        codeBlockLang = ctx.language;
        if (codeBlockLang) {
          parts.push(
            `<pre><code class="language-${escapeHTML(codeBlockLang)}">`
          );
        } else {
          parts.push('<pre>');
        }
      } else {
        const [openTag] = getElementTags(ctx);
        if (openTag) parts.push(openTag);
      }
    }

    previousContext = currentContext;

    // Render the segment content
    const renderedText = renderSegmentWithFormats(
      segment.text,
      segment.formats,
      segmentInCodeBlock
    );
    parts.push(renderedText);
  }

  // Close any remaining open contexts
  for (let i = previousContext.length - 1; i >= 0; i--) {
    const ctx = previousContext[i];
    if (!ctx) continue;
    if (ctx.type === 'code_block') {
      if (codeBlockLang) {
        parts.push('</code></pre>');
      } else {
        parts.push('</pre>');
      }
    } else {
      const [, closeTag] = getElementTags(ctx);
      if (closeTag) parts.push(closeTag);
    }
  }

  return parts.join('');
}

/**
 * Convert a Lexical EditorState to a Telegram-compatible HTML string.
 * The output uses only the HTML tags that Telegram's HTML parse mode
 * supports. No CSS, no classes (except `language-*` on code blocks),
 * no data attributes.
 *
 * @param editorState - The Lexical editor state to export.
 * @returns A string of Telegram-compatible HTML.
 */
export function exportToTelegramHTML(editorState: EditorState): string {
  const segments = walkTree(editorState);
  const html = buildHTML(segments);

  // Trim trailing newlines
  return html.replace(/\n+$/, '');
}
